import { Card, TableBody, TableCell, TableRow } from "@material-ui/core";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { ComboById_comboById } from "@saleor/combos/types/ComboById";
import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps2, ReorderAction, TabListActions } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ComboProducts from "../ComboProducts/ComboProducts";

export interface ComboDetailsProps
  extends Pick<ListProps2, Exclude<keyof ListProps2, "onRowClick">>,
    TabListActions<"variantListToolbar"> {
  combo: ComboById_comboById;
  onSubmit: (data: ComboFormData) => void;
  onBack: () => void;
  onDelete?: () => void;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  activeTab?: ComboDetailsPageTab;
  onTabClick?: (index: ComboDetailsPageTab) => void;
  onProductAssign?: () => void;
  onProductUnassign?: (id: string) => void;
  onProductClick?: (productId: string) => () => void;
  onProductReorder?: ReorderAction;
  redirectOrder?: (id: string) => () => void;
}

export interface ProductsData {
  productId: string;
  discountPrice: string;
  sortOrder: any;
}

export interface ComboFormData {
  name: string;
  totalPrice: any;
  labelText: string;
  labelLink: string;
  enable: boolean;
}

export enum ComboDetailsPageTab {
  products = "products"
}

export function comboDetailsPageTab(tab: string): ComboDetailsPageTab {
  return tab === ComboDetailsPageTab.products
    ? ComboDetailsPageTab.products
    : ComboDetailsPageTab.products;
}

const ProductsTab = Tab(ComboDetailsPageTab.products);

const ComboDetails: React.FC<ComboDetailsProps> = ({
  combo,
  onSubmit,
  disabled,
  onBack,
  onDelete,
  activeTab,
  onTabClick,
  saveButtonBarState,
  onProductAssign,
  onProductUnassign,
  onProductReorder,
  onProductClick,
  isChecked,
  selected,
  toggle,
  toggleAll,
  variantListToolbar,
  pageInfo,
  redirectOrder
}) => {
  const initialForm: ComboFormData = {
    name: maybe(() => combo.name, ""),
    totalPrice: maybe(() => combo.totalPrice, 0),
    labelText: maybe(() => combo.labelText, ""),
    labelLink: maybe(() => combo.labelLink, ""),
    enable: maybe(() => combo.enable, false)
  };
  const intl = useIntl();
  return (
    <>
      <Form initial={initialForm} onSubmit={onSubmit}>
        {({ change, data, hasChanged, submit }) => (
          <Container>
            <AppHeader onBack={onBack}>
              <FormattedMessage {...sectionNames.combo} />
            </AppHeader>
            <PageHeader title={"Product Sets"} />
            <Grid>
              <div>
                <Card>
                  <CardTitle
                    title={intl.formatMessage(
                      commonMessages.generalInformations
                    )}
                  />
                  <CardContent>
                    <TextField
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "Name",
                        description: "Name"
                      })}
                      name={"name" as keyof ComboFormData}
                      fullWidth
                      value={data.name}
                      onChange={change}
                    />
                    <FormSpacer />
                    <TextField
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "Total Price",
                        description: "totalPrice"
                      })}
                      name={"totalPrice" as keyof ComboFormData}
                      fullWidth
                      value={data.totalPrice}
                      onChange={change}
                    />
                    <FormSpacer />
                    <TextField
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "Label Text",
                        description: "labelText"
                      })}
                      name={"labelText" as keyof ComboFormData}
                      fullWidth
                      value={data.labelText}
                      onChange={change}
                    />
                    <FormSpacer />
                    <TextField
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "Label Link",
                        description: "labelLink"
                      })}
                      name={"labelLink" as keyof ComboFormData}
                      fullWidth
                      value={data.labelLink}
                      onChange={change}
                    />
                    <FormSpacer />
                    <ControlledCheckbox
                      name={"enable" as keyof ComboFormData}
                      label={intl.formatMessage({
                        defaultMessage: "Enable",
                        description: "enable"
                      })}
                      checked={data.enable}
                      onChange={change}
                      disabled={disabled}
                    />
                  </CardContent>
                </Card>
                <CardSpacer />
                {combo && (
                  <>
                    <TabContainer>
                      <ProductsTab
                        isActive={activeTab === ComboDetailsPageTab.products}
                        changeTab={onTabClick}
                      >
                        {intl.formatMessage({
                          defaultMessage: "Product",
                          description: "number of Product",
                          id: "comboDetailsPageProductsQuantity"
                        })}
                      </ProductsTab>
                    </TabContainer>
                    <CardSpacer />
                    <p>
                      {" "}
                      {combo?.products?.totalCount} products included,total
                      price is US${combo.totalPrice}
                    </p>
                    <br />
                    <ComboProducts
                      onProductReorder={onProductReorder}
                      disabled={disabled}
                      onNextPage={null}
                      onPreviousPage={null}
                      onProductAssign={onProductAssign}
                      onProductUnassign={onProductUnassign}
                      pageInfo={pageInfo}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={variantListToolbar}
                      onChange={change}
                      combo={combo}
                      onRowClick={onProductClick}
                    />
                    <br />
                    <p>
                      {" "}
                      it is recommended to configure no more than 3 products
                    </p>
                  </>
                )}
              </div>
              <div>
                <Card style={{ overflow: "visible" }}>
                  <CardTitle
                    title={
                      intl.formatMessage({ defaultMessage: "Order List:" }) +
                      maybe(() => combo.orderNumber, 0)
                    }
                  />
                  {combo?.orderList ? (
                    <CardContent>
                      <TableBody style={{ width: "400px" }}>
                        {renderCollection(combo.orderList, order => (
                          <TableRow
                            key={order.id}
                            style={{ width: "400px" }}
                            onClick={redirectOrder(order.id)}
                          >
                            <TableCell style={{ width: "400px" }}>
                              {order.number}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </CardContent>
                  ) : (
                    <CardContent>
                      <Typography>
                        <FormattedMessage
                          defaultMessage="No have order buy!"
                          description="no order list"
                        />
                      </Typography>
                    </CardContent>
                  )}
                </Card>
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              state={saveButtonBarState}
              onCancel={onBack}
              onSave={submit}
              onDelete={combo === null ? undefined : onDelete}
            />
          </Container>
        )}
      </Form>
    </>
  );
};
export default ComboDetails;
